import React from 'react';

import { DefaultContext } from '@apollo/client';

import { mergeObjects } from '@hrlogics/helper-utils';

import { Nullable } from '@clearverify/types';

import {
  EmployerDocument,
  EmployerQuery,
  EmployerQueryVariables,
  EmployerUpdate,
  useEmployerQuery,
  useEmployerUpdateMutation,
} from 'src/api';

import { useNotification } from '../app';

export const useEmployerById = (employerId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = useEmployerQuery({
    variables: {
      id: String(employerId),
    },
    skip: !employerId,
  });

  const employer = React.useMemo(() => data?.employer, [data?.employer]);

  const [employerUpdateMutation, { loading: updating }] = useEmployerUpdateMutation({
    context: createApolloContext({
      errorMessage: "Can't update Company Information",
      successMessage: 'Company Information has been successfully updated',
    }),
    onCompleted: (data, clientOptions) => {
      if (!employerId) return;
      const queryOptions = {
        query: EmployerDocument,
        variables: {
          id: employerId,
        } satisfies EmployerQueryVariables,
      };
      const queryData = clientOptions?.client?.readQuery<EmployerQuery, EmployerQueryVariables>(
        queryOptions,
      );
      try {
        if (queryData) {
          clientOptions?.client?.writeQuery<EmployerQuery, EmployerQueryVariables>({
            ...queryOptions,
            data: mergeObjects<EmployerQuery>(queryData, { employer: data.employerUpdate }),
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
  });

  const updateEmployer = async (params: {
    data: Omit<EmployerUpdate, 'id'>;
    context?: DefaultContext;
  }) => {
    const { data, context } = params;

    await employerUpdateMutation({
      variables: {
        where: { id: employerId },
        data,
      },
      context,
    });
  };

  return {
    employer,
    employerId,
    updateEmployer,
    loading,
    updating,
  };
};
